import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isTaskOverdue } from '@/lib/task-filters';
import type { ClaimTask } from '@/api/generated/client';
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  isSameDay, 
  isToday,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isSameMonth
} from 'date-fns';

interface TaskCalendarProps {
  tasks: ClaimTask[];
  onDateSelect?: (date: Date) => void;
  selectedDate?: Date;
}

export function TaskCalendar({ tasks, onDateSelect, selectedDate }: TaskCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getTasksForDate = (date: Date) => {
    return tasks.filter(task => {
      if (!task.dueDate) return false;
      return isSameDay(new Date(task.dueDate), date);
    });
  };

  const getTasksStatus = (tasks: ClaimTask[]) => {
    const hasOverdue = tasks.some(task => isTaskOverdue(task));
    const hasRegular = tasks.some(task => !isTaskOverdue(task));
    
    return { hasOverdue, hasRegular };
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleDateClick = (date: Date) => {
    onDateSelect?.(date);
  };

  return (
    <div className="border rounded-lg p-4 bg-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevMonth}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleNextMonth}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Week days */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div
            key={day}
            className="text-center text-sm font-medium text-muted-foreground p-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          const dayTasks = getTasksForDate(day);
          const { hasOverdue, hasRegular } = getTasksStatus(dayTasks);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isTodayDate = isToday(day);

          return (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              className={cn(
                'relative p-2 h-14 text-sm rounded-md transition-colors',
                'hover:bg-accent',
                !isCurrentMonth && 'text-muted-foreground opacity-50',
                isTodayDate && 'bg-primary/10 font-semibold',
                isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90'
              )}
            >
              <div className="flex flex-col items-center justify-center h-full">
                <span>{format(day, 'd')}</span>
                {dayTasks.length > 0 && (
                  <div className="flex gap-0.5 mt-1">
                    {hasOverdue && (
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                    )}
                    {hasRegular && !hasOverdue && (
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

    </div>
  );
}
